import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { getRequest, patchRequest, postRequest } from 'api/apiClient';
import { hasPermission, transformNullToEmptyString } from 'utils/utils';
import axios from 'axios';
import { notification } from 'antd';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpFormDrawer } from 'components/customAntd/DLS/OpFormDrawer/OpFormDrawer';
import { DRAWER_WIDTH } from 'constants/ui';
import {
    KIOSK_IN_WORKFLOW_TYPE_ID,
    KIOSK_OUT_WORKFLOW_TYPE_ID,
    SIGN_IN_WORKFLOW_TYPE_ID,
    SIGN_OUT_WORKFLOW_TYPE_ID,
} from 'constants/workflowTypes';
import { VisitorWorkflow, VisitorWorkflowType } from 'types/visitorWorkflowTypes';
import { Location, LocationWorkflow } from 'types/locationTypes';
import { VideoDeviceType } from 'types/videoDeviceTypes';
import { clearDevicesByWorkflow, resetAltaVideoForOrg, resetThumbnailPresense } from 'store/slices/altaVideoSlice';
import { describeLocation, describeLocationWorkflow, fetchLocations } from 'store/slices/locationsSlice';
import { LOCATION_CREATED_ID, LOCATION_EDITED_ID } from 'constants/userActivities';
import LocationForm from './tabs/LocationForm';
import WorkflowForm from './tabs/WorkflowForm';
import DetailsForm from './tabs/DetailsForm';
import VideoForm from './tabs/VideoForm';
import SelfSignInForm from './tabs/SelfSignInForm';
import { useTranslation } from 'react-i18next';
import { VisitorType } from 'types/VisitorTypeTypes';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

interface LocationsDrawerProps {
    open: boolean;
    onClose: () => void;
    selectedLocation: Location | null;
}

const LocationsDrawer: React.FC<LocationsDrawerProps> = ({ open, onClose, selectedLocation }) => {
    const dispatch: AppDispatch = useDispatch();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const hasLocationWrite = hasPermission(tokenScopeList, orgId, 'o', 'location:w');

    const [activeKey, setActiveKey] = useState<string>('location');
    const [visitorWorkflows, setVisitorWorkflows] = useState<VisitorWorkflow[]>([]);
    const [workflowTypes, setWorkflowTypes] = useState<VisitorWorkflowType[]>([]);
    const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
    const [selectedLocationWorkflow, setSelectedLocationWorkflow] = useState<LocationWorkflow | null>(null);
    const [selectedLocationWorkflowByVisitorType, setSelectedLocationWorkflowByVisitorType] = useState<Record<string, any[]>>({});
    const [videoDevices, setVideoDevices] = useState<VideoDeviceType[]>([]);
    const [devicesByWorkflow, setDevicesByWorkflow] = useState<Record<string, string[]>>({});
    const [isAltaVideoInstalled, setIsAltaVideoInstalled] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [form] = OpForm.useForm();
    const { t } = useTranslation();

    useEffect(() => {
        if (open) {
            setActiveKey('location');
        }
    }, [open]);

    // Fetch workflows
    useEffect(() => {
        const fetchVisitorWorkflows = async () => {
            setLoading(true);
            try {
                const visitorWorkflows = await getRequest(`/orgs/${orgId}/visitorWorkflow`);
                const visitorWorkflowTypes = await getRequest(`/orgs/${orgId}/visitorWorkflowTypes`);
                setVisitorWorkflows(visitorWorkflows.data);
                setWorkflowTypes(visitorWorkflowTypes.data);
            } catch (error) {
                console.log("Failed to fetch visitor workflows.");
            } finally {
                setLoading(false);
            }
        };
        fetchVisitorWorkflows();

        if (selectedLocation) {
            const fetchLocationWorkflows = async () => {
                setLoading(true);
                try {
                    const visitorTypes = await getRequest(`/orgs/${orgId}/visitorTypes`);
                    const locationWorkflow = await getRequest(`/orgs/${orgId}/sites/${selectedLocation.id}/workflows`);
                    const locationVisitorType = await getRequest(`/orgs/${orgId}/sites/${selectedLocation.id}/visitorType`);

                    const workflowByVisitorTypeMap = {
                        SignInWorkflowId: "1",
                        SignOutWorkflowId: "2",
                        KioskInWorkflowId: "3",
                        KioskOutWorkflowId: "4",
                    }

                    // Initialize empty arrays for each workflow type
                    const workflowByVisitorTypeValues: Record<string, any[]> = {
                        "1": [],
                        "2": [],
                        "3": [],
                        "4": [],
                    };

                    // Convert Workflow by Visitor Type into form-friendly structure
                    locationVisitorType.data.forEach((visitorTypes: any) => {
                        const visitorTypeId = visitorTypes.id;
                        const workflow = visitorTypes.workflow || {};

                        Object.entries(workflowByVisitorTypeMap).forEach(([workflowKey, formListName]) => {
                            const workflowId = workflow[workflowKey];
                            if (workflowId && workflowId !== -1) {
                                // Merge if same workflow exists, else add a new one
                                const existingItem = workflowByVisitorTypeValues[formListName].find(
                                    (item) => item.workflowId === workflowId
                                );
                                if (existingItem) {
                                    existingItem.visitorTypeId.push(visitorTypeId);
                                } else {
                                    workflowByVisitorTypeValues[formListName].push({
                                        visitorTypeId: [visitorTypeId],
                                        workflowId: workflowId,
                                    });
                                }
                            }
                        });
                    });

                    setVisitorTypes(visitorTypes.data);
                    setSelectedLocationWorkflow(locationWorkflow.data[0]);
                    setSelectedLocationWorkflowByVisitorType(workflowByVisitorTypeValues);
                } catch (error) {
                    console.log("Failed to fetch location workflow.");
                } finally {
                    setLoading(false);
                }
            };
            fetchLocationWorkflows();
        }

        // Fetch video devices (if installed)
        const fetchAltaVideoIntegration = async () => {
            try {
                const integrationData = await getRequest(`/orgs/${orgId}/integrationMsiAltaVideo`);
                if (Array.isArray(integrationData.data) && integrationData.data.length === 0) {
                    setIsAltaVideoInstalled(false);
                    return;
                } else {
                    setIsAltaVideoInstalled(true);
                }

                const fetchVideoDevices = async () => {
                    setLoading(true);
                    try {
                        const videoDevices = await getRequest(`/orgs/${orgId}/integrationMsiAltaVideo/videoDevices`);
                        setVideoDevices(videoDevices.data);
                    } catch (error) {
                        console.log("Failed to fetch video devices.");
                    } finally {
                        setLoading(false);
                    }
                };
                fetchVideoDevices();

                if (selectedLocation) {
                    const fetchDevicesByWorkflow = async () => { // fetch selected guId from all workflows
                        setLoading(true);
                        try {
                            const devicesByWorkflow = await getRequest(`/orgs/${orgId}/sites/${selectedLocation.id}/MsiAltaVideoDevice`);
                            const data = devicesByWorkflow.data;
                            setDevicesByWorkflow({
                                signInDeviceId: data.signInDeviceId ?? [],
                                signOutDeviceId: data.signOutDeviceId ?? [],
                                kioskInDeviceId: data.kioskInDeviceId ?? [],
                                kioskOutDeviceId: data.kioskOutDeviceId ?? [],
                            });
                        } catch (error) {
                            console.log("Failed to fetch selected video devices.");
                        } finally {
                            setLoading(false);
                        }
                    };
                    fetchDevicesByWorkflow();
                }
            } catch (error) {
                console.error("Error fetching Alta Video integration data: ", error);
            }
        }
        fetchAltaVideoIntegration();

    }, [orgId, selectedLocation]);

    const initialValues = selectedLocation ? {
        ...selectedLocation,
        ...(selectedLocationWorkflow || {}),
        ...selectedLocationWorkflowByVisitorType,
        ...devicesByWorkflow,
    } : {
        timezone: tokenScopeList.find(s => Number(s.org.id) === Number(orgId))?.org?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        SignInWorkflowId: visitorWorkflows.find(workflow => workflow.workflowTypeId === SIGN_IN_WORKFLOW_TYPE_ID)?.id,
        SignOutWorkflowId: visitorWorkflows.find(workflow => workflow.workflowTypeId === SIGN_OUT_WORKFLOW_TYPE_ID)?.id,
        KioskInWorkflowId: visitorWorkflows.find(workflow => workflow.workflowTypeId === KIOSK_IN_WORKFLOW_TYPE_ID)?.id,
        KioskOutWorkflowId: visitorWorkflows.find(workflow => workflow.workflowTypeId === KIOSK_OUT_WORKFLOW_TYPE_ID)?.id,
        SelfSignIn: 0,
        SelfSignOut: 0,
    };

    const handleRevokeSuccess = useCallback(() => {
        // Update form values to reflect disabled state
        // API calls and notifications are handled in SelfSignInForm
        form.setFieldsValue({
            SelfSignIn: false,
            SelfSignOut: false
        });
    }, [form]);

    const tabItems = [
        {
            key: 'location',
            label: t('location.tabs.location'),
            children: <LocationForm />,
        },
        {
            key: 'details',
            label: t('location.tabs.details'),
            children: <DetailsForm />,
        },
        {
            key: 'workflow',
            label: t('location.tabs.workflow'),
            children: <WorkflowForm form={form} visitorWorkflows={visitorWorkflows} workflowTypes={workflowTypes} visitorTypes={visitorTypes} />,
        },
        ...(isAltaVideoInstalled ? [{
            key: 'video',
            label: t('location.tabs.video'),
            children: <VideoForm workflowTypes={workflowTypes} videoDevices={videoDevices} />,
        }] : []),
        ...(selectedLocation ? [{
            key: 'selfSignIn',
            label: t('location.tabs.selfSignIn') || 'Self Sign In',
            children: <SelfSignInForm selectedLocationId={selectedLocation?.id} onRevokeSuccess={handleRevokeSuccess} form={form} />,
        }] : []),
    ];

    const updateWorkflowByVisitorType = async (orgId: number, locationId: number, visitorTypes: VisitorType[], workflowByVisitorTypeValues: Record<string, any[]>) => {
        if (Object.keys(workflowByVisitorTypeValues).length === 0) return;

        const workflowByVisitorTypeGrouped: Record<string, any> = {};

        visitorTypes.forEach(({ id }) => {
            workflowByVisitorTypeGrouped[id] = {
                SignInWorkflowId: -1,
                SignOutWorkflowId: -1,
                KioskInWorkflowId: -1,
                KioskOutWorkflowId: -1,
            };
        });

        // Convert Workflow by Visitor Type into API-friendly structure
        Object.entries(workflowByVisitorTypeValues).forEach(([workflowTypeId, entries]) => {
            const items = Array.isArray(entries) ? entries : [];

            items.forEach(entry => {
                if (!entry || !entry.visitorTypeId || !entry.workflowId) return;

                const visitorTypeIds = entry.visitorTypeId;
                const workflowId = entry.workflowId;

                visitorTypeIds.forEach((visitorTypeId: number) => {
                    if (!workflowByVisitorTypeGrouped[visitorTypeId]) {
                        workflowByVisitorTypeGrouped[visitorTypeId] = {};
                    }

                    switch (Number(workflowTypeId)) {
                        case 1:
                            workflowByVisitorTypeGrouped[visitorTypeId].SignInWorkflowId = workflowId;
                            break;
                        case 2:
                            workflowByVisitorTypeGrouped[visitorTypeId].SignOutWorkflowId = workflowId;
                            break;
                        case 3:
                            workflowByVisitorTypeGrouped[visitorTypeId].KioskInWorkflowId = workflowId;
                            break;
                        case 4:
                            workflowByVisitorTypeGrouped[visitorTypeId].KioskOutWorkflowId = workflowId;
                            break;
                    }
                });
            });
        });

        await Promise.all(
            Object.entries(workflowByVisitorTypeGrouped).map(([visitorTypeId, workflows]) =>
                patchRequest(`/orgs/${orgId}/sites/${locationId}/visitorType/${visitorTypeId}`, workflows)
            )
        );
    };

    const handleSubmit = useCallback(async ({ values, touchedValues }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        try {
            if (touchedValues && Object.keys(touchedValues).length > 0) {
                const workflowFields = ['SignInWorkflowId', 'SignOutWorkflowId', 'KioskInWorkflowId', 'KioskOutWorkflowId', 'SelfSignIn', 'SelfSignOut'];
                const videoFields = ['signInDeviceId', 'signOutDeviceId', 'kioskInDeviceId', 'kioskOutDeviceId'];

                const workflowValues = Object.fromEntries(
                    workflowFields
                        .filter((field) => touchedValues.hasOwnProperty(field))
                        .map((field) => {
                            // Convert boolean to number for SelfSignIn/SelfSignOut
                            if (field === 'SelfSignIn' || field === 'SelfSignOut') {
                                return [field, touchedValues[field] ? 1 : 0];
                            }
                            return [field, touchedValues[field]];
                        })
                );
                const workflowByVisitorTypeValues = Object.fromEntries(
                    Object.keys(form.getFieldsValue())
                        .filter((key) => !isNaN(Number(key)))
                        .map((key) => [key, form.getFieldsValue()[key]])
                );

                const videoValues = Object.fromEntries(
                    videoFields
                        .filter((field) => touchedValues.hasOwnProperty(field))
                        .map((field) => [field, touchedValues[field]])
                );

                const transformedValues = Object.fromEntries(
                    Object.entries(transformNullToEmptyString(touchedValues)).filter(
                        ([key]) => !workflowFields.includes(key) && isNaN(Number(key)) && !videoFields.includes(key)
                    )
                );

                if (selectedLocation) {
                    await patchRequest(`/orgs/${orgId}/sites/${selectedLocation.id}`, transformedValues);

                    // Default workflows
                    if (Object.keys(workflowValues).length > 0) {
                        await patchRequest(`/orgs/${orgId}/sites/${selectedLocation.id}/workflows`, workflowValues);

                        // Check if Self Sign In or Self Sign Out is being enabled
                        const isSelfSignInEnabled = workflowValues.hasOwnProperty('SelfSignIn') && workflowValues.SelfSignIn === 1;
                        const isSelfSignOutEnabled = workflowValues.hasOwnProperty('SelfSignOut') && workflowValues.SelfSignOut === 1;

                        // Generate self sign token if either is being enabled
                        if (isSelfSignInEnabled || isSelfSignOutEnabled) {
                            try {
                                // Check if token already exists
                                const existingToken = await getRequest(`/orgs/${orgId}/sites/${selectedLocation.id}/selfSignToken`);
                                const hasToken = existingToken?.data?.length > 0;

                                // Only create token if it doesn't exist
                                if (!hasToken) {
                                    await postRequest(`/orgs/${orgId}/sites/${selectedLocation.id}/selfSignToken`, {});
                                }
                            } catch (error) {
                                console.error("Failed to generate self sign token:", error);
                            }
                        }
                    }

                    // Workflow By Visitor Types
                    await updateWorkflowByVisitorType(orgId, selectedLocation.id, visitorTypes, workflowByVisitorTypeValues);

                    await postRequest(`/orgs/${orgId}/userActivity`, {
                        userId: globalUserId,
                        activityId: LOCATION_EDITED_ID,
                        details: values.name
                    });

                    if (Object.keys(videoValues).length > 0) {
                        await patchRequest(`/orgs/${orgId}/sites/${selectedLocation.id}/MsiAltaVideoDevice`, {
                            ...form.getFieldsValue(videoFields), // new value
                            ...videoValues // existing value
                        });
                    }

                    // reset video presense so that it can refetch thumbnails in visitors table
                    dispatch(resetAltaVideoForOrg());
                    dispatch(clearDevicesByWorkflow());
                    dispatch(resetThumbnailPresense());

                    if (selectedLocation.id === globalLocationId) {
                        await dispatch(describeLocation({ orgId, locationId: selectedLocation.id, global: true }));
                        await dispatch(describeLocationWorkflow({ orgId, locationId: selectedLocation.id, global: true }));
                    }

                    notification.success({
                        message: t('notification.success'),
                        description: t('location.notification.updateSuccess'),
                        placement: 'bottomRight',
                    });
                } else {
                    let timezone = null;
                    try {
                        const tokenIdentity = await axios.get(`${apiUrl}/auth/accessTokens/${localStorage.getItem('authToken')}`);
                        timezone = values.timezone || tokenIdentity.data.data[0].tokenScopeList.find((scope: any) => Number(scope.org.id) === Number(orgId)).org.timezone
                            || Intl.DateTimeFormat().resolvedOptions().timeZone;
                    } catch {
                        console.log("Failed to fetch timezone.")
                    }
                    // create a new location
                    const response = await postRequest(`/orgs/${orgId}/sites`, {
                        ...transformedValues,
                        timezone,
                    });

                    // Default workflows
                    const selfSignInValue = values.SelfSignIn ?? initialValues.SelfSignIn;
                    const selfSignOutValue = values.SelfSignOut ?? initialValues.SelfSignOut;

                    await patchRequest(`/orgs/${orgId}/sites/${response.data[0].id}/workflows`, {
                        SignInWorkflowId: values.SignInWorkflowId ? values.SignInWorkflowId : initialValues.SignInWorkflowId,
                        SignOutWorkflowId: values.SignOutWorkflowId ? values.SignOutWorkflowId : initialValues.SignOutWorkflowId,
                        KioskInWorkflowId: values.KioskInWorkflowId ? values.KioskInWorkflowId : initialValues.KioskInWorkflowId,
                        KioskOutWorkflowId: values.KioskOutWorkflowId ? values.KioskOutWorkflowId : initialValues.KioskOutWorkflowId,
                        SelfSignIn: selfSignInValue ? 1 : 0,
                        SelfSignOut: selfSignOutValue ? 1 : 0
                    });

                    // Generate self sign token if either self sign in or out is enabled
                    if (selfSignInValue || selfSignOutValue) {
                        try {
                            await postRequest(`/orgs/${orgId}/sites/${response.data[0].id}/selfSignToken`, {});
                        } catch (error) {
                            console.error("Failed to generate self sign token:", error);
                        }
                    }

                    // Workflow by visitor types
                    await updateWorkflowByVisitorType(orgId, response.data[0].id, visitorTypes, workflowByVisitorTypeValues);

                    await patchRequest(`/orgs/${orgId}/sites/${response.data[0].id}/MsiAltaVideoDevice`, {
                        ...form.getFieldsValue(videoFields), // new value
                    });

                    await postRequest(`/orgs/${orgId}/userActivity`, {
                        userId: globalUserId,
                        activityId: LOCATION_CREATED_ID,
                        details: values.name
                    });

                    notification.success({
                        message: t('notification.success'),
                        description: t('location.notification.createSuccess'),
                        placement: 'bottomRight',
                    });
                }

                // Re-fetch the updated list of locations
                dispatch(fetchLocations({ orgId, status: 1 }));
            }
            form.resetFields();
            onClose();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('location.notification.saveError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [dispatch, onClose, form, globalUserId, globalLocationId, orgId, visitorTypes, initialValues.KioskInWorkflowId, initialValues.KioskOutWorkflowId, initialValues.SignInWorkflowId, initialValues.SignOutWorkflowId, initialValues.SelfSignIn, initialValues.SelfSignOut, selectedLocation, t]);

    return (
        <OpFormDrawer
            form={form}
            title={selectedLocation ? selectedLocation.name : t('location.addTitle')}
            width={DRAWER_WIDTH}
            open={open}
            onClose={onClose}
            isFormLoading={loading || isSubmitting}
            isFormReadOnly={!hasLocationWrite}
            formComponent={
                <OpForm
                    form={form}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    hasError={false}
                    defaultButtons={false}
                    isReadOnly={!hasLocationWrite}
                >
                    <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                </OpForm>
            }
        />
    );
};

export default LocationsDrawer;
